<script>
/**
 * 시세 차트 — HQ 전용.
 *
 * **보는 값**이 둘이고 어느 금속이든 둘 다 있다(2026-10-19 결정:
 * "전 금속 통일 — 국내는 원화, 해외는 USD").
 *
 * <pre>
 *   국내   우리가 파는 값   원/g       세 줄(기본·살때·팔때), 수수료 포함
 *   해외   바깥 시장 값     USD/ozt    한 줄(시장 시세), 환율도 수수료도 없음
 * </pre>
 *
 * 그전에는 토글이 **기준**(어느 시장에서 왔나)이었다. 금만 KRX·해외 둘이라
 * 은·백금에는 토글이 아예 안 떴고, "국내시세가 왜 없냐" 는 제보로 돌아왔다 —
 * 은·백금도 화면의 값이 국내 고시가인데 라벨이 "해외" 였기 때문이다.
 *
 * ★ 국내 시세를 어디서 가져오는지는 금속마다 다르다. 금은 KRX 금현물,
 *   은·백금은 KRX 에 시장이 없어 해외 현물을 원화로 환산한 것이다.
 *   그 차이는 {@code METALS[].domesticBasis} 하나로 끝난다.
 *
 * ★ 해외는 **USD 원본이 있는 점만** 그린다. 2026-10-18 이전은 metals.dev 로
 *   이미 원화 환산된 값만 받아 둬서 달러를 되살릴 수 없다. 조용히 빼지 않고
 *   몇 점이 빠졌는지 화면이 말해 준다.
 *
 * ★ 수수료는 여기서 붙이지 않는다. 공용 서버가 **점마다 그 시점의 수수료**를
 *   찾아 붙여 내려 준다. 지난 수수료를 아는 쪽이 거기뿐이고, 화면이 지금 값으로
 *   과거까지 칠하면 "그때 얼마에 팔았나" 가 틀린다.
 *
 * ★ 아래 등급별 시세표만 원/돈이다. 돈 값은 환산율과 무게를 곱한 뒤 서버가 한 번만
 *   깎아 내려 준 것이라, 화면에서 3.75 를 곱해 만들면 끝자리가 달라진다. 섞지 않는다.
 */
import * as echarts from 'echarts'
import { getPriceHistory, getPriceBoard } from '@/api/marketChart'

// 서버의 성공 코드는 0 이다(ResultCode.SUCCESS). 200 이 아니다 — 여기서 틀리면
// 모든 읽기가 조용히 실패한다. 수수료 화면도 같은 값을 쓴다.
const OK = 0

const TENANTS = [
  { key: 'GOLDNARA', label: '골드나라' },
  { key: 'AURA', label: '아우라' },
]

/**
 * 금속마다 **국내 시세를 어느 시장에서 가져오나**.
 *
 * 금은 KRX 금현물이 있어 거기서 오고, 은·백금은 KRX 에 시장이 없어 해외 현물을
 * 원화로 환산한 것이 국내 시세다. 화면의 "국내/해외" 는 이 둘을 가르는 것이 아니라
 * **보는 값의 통화**를 가른다 - 국내는 원/g(수수료 포함), 해외는 바깥 시장의 USD 다
 * (2026-10-19 결정: "전 금속 통일 — 국내는 원화, 해외는 USD").
 */
const METALS = [
  { key: 'GOLD', label: '금', domesticBasis: 'KRX' },
  { key: 'SILVER', label: '은', domesticBasis: 'SPOT' },
  { key: 'PLATINUM', label: '백금', domesticBasis: 'SPOT' },
]

/** 보는 방식. 어느 금속이든 둘 다 있다. */
const VIEWS = [
  { key: 'domestic', label: '국내' },
  { key: 'overseas', label: '해외' },
]

/**
 * 지금 보고 있는 것의 설명. 금속마다 국내 시세의 출처가 달라서 금속까지 받는다.
 *
 * <b>국내</b>는 우리가 파는 값이다 — 원/g 에 수수료가 붙어 있고 세 줄(기본·살때·팔때)이 있다.
 * <b>해외</b>는 바깥 시장이 매긴 값이다 — USD/트로이온스 한 줄이고 수수료가 없다.
 * 우리가 달러로 팔지 않으니 거기에 살때·팔때를 얹는 것은 뜻이 없다.
 */
function viewInfo(metal, view) {
  if (view === 'overseas') {
    return {
      label: '해외 (현물 USD/트로이온스)',
      hint: '바깥 시장이 매긴 값 그대로다. 환율도 수수료도 붙지 않는다',
      unit: 'USD/ozt',
      domestic: false,
    }
  }
  if (metal === 'GOLD') {
    return {
      label: '국내 (KRX 금현물)',
      hint: '원/g 으로 바로 온다. 환율을 적용하지 않는다',
      unit: '원/g',
      domestic: true,
    }
  }
  return {
    label: '국내 (해외 현물을 원화로 환산)',
    hint: 'KRX 에 시장이 없어 해외 USD/트로이온스를 환율로 환산한다. 이 값이 국내 고시가다',
    unit: '원/g',
    domestic: true,
  }
}

const RANGES = [
  { key: '1m', label: '1개월', days: 30 },
  { key: '3m', label: '3개월', days: 90 },
  { key: '6m', label: '6개월', days: 180 },
  { key: '1y', label: '1년', days: 365 },
  { key: 'all', label: '전체', days: null },
]

/**
 * 세 줄의 색.
 *
 * 처음에는 수수료 화면의 글자색(#ffb4a2 · #9ecbff)을 그대로 썼는데 **선에서는
 * 구분이 안 됐다**(2026-10-19 제보). 두 색 다 연하고 밝기가 비슷해서, 글자로는
 * 읽히지만 2px 선으로는 겹쳐 보인다. 글자와 선은 필요한 것이 다르다 - 글자는
 * 바탕과의 대비만 있으면 되고 선은 **서로** 달라야 한다.
 *
 * 그래서 색맛(계열)은 그대로 두고 진하게만 했다. 살 때는 여전히 붉은 쪽,
 * 팔 때는 푸른 쪽이라 수수료 화면과 같은 뜻으로 읽힌다. 기본가격은 둘 사이에
 * 끼므로 아예 흰색으로 떼어 놓았다.
 */
const LINES = [
  { key: 'base', name: '기본가격', color: '#ffffff' },
  { key: 'buy', name: '살 때', color: '#ff6b5b' },
  { key: 'sell', name: '팔 때', color: '#4fa8ff' },
]

/**
 * 해외로 볼 때의 한 줄. 바깥 시장이 매긴 값이라 살때·팔때가 없다 -
 * 우리가 달러로 팔지 않으니 거기에 수수료를 얹는 것은 뜻이 없다.
 */
const OVERSEAS_LINE = { key: 'rawPrice', name: '시장 시세', color: '#ffd166' }

/** 선 굵기. 2 로는 가늘어 색이 덜 드러났다(2026-10-19 제보). */
const LINE_WIDTH = 3

export default {
  name: 'MarketChartPage',

  data() {
    return {
      TENANTS,
      METALS,
      VIEWS,
      RANGES,
      LINES,

      tenant: 'GOLDNARA',
      metal: 'GOLD',
      // 'domestic' 우리가 파는 값(원/g) / 'overseas' 바깥 시장 값(USD/ozt)
      view: 'domestic',
      range: '3m',
      interval: 'day',
      // 'overlay' 한 장에 세 줄 / 'split' 세 장으로 나눠
      mode: 'overlay',

      loading: false,
      errorMessage: '',

      points: [],
      // 수수료 정책이 걸려 있지 않은 구간이 있으면 화면이 그 사실을 말해야 한다.
      priced: true,

      boardRows: [],
      boardAt: '',
      boardLoading: false,

      // ECharts 인스턴스. 겹쳐 보기는 한 개, 나눠 보기는 세 개다.
      charts: [],
    }
  },

  computed: {
    metalInfo() {
      return METALS.find((m) => m.key === this.metal) || METALS[0]
    },

    /** 지금 보고 있는 것의 설명. */
    viewText() {
      return viewInfo(this.metal, this.view)
    },

    /** 국내인가. 해외면 줄이 하나(시장값)뿐이라 화면이 여러 군데 달라진다. */
    isDomestic() {
      return this.view === 'domestic'
    },

    /**
     * 실제로 그릴 점.
     *
     * 해외는 **USD 원본이 있는 점만** 쓴다. metals.dev 로 받아 둔 7개월은 이미
     * 원화로 환산된 값만 저장돼 있어(raw_currency = KRW) USD 를 복원할 수 없다.
     * 지어내지 않고 뺀다 - 대신 화면이 몇 점이 빠졌는지 말해 준다.
     */
    shown() {
      if (this.isDomestic) return this.points
      return this.points.filter((p) => p.rawCurrency === 'USD' && p.rawPrice != null)
    },

    /** 해외로 볼 때 USD 원본이 없어 빠진 점 수. */
    droppedForUsd() {
      return this.isDomestic ? 0 : this.points.length - this.shown.length
    },

    /** 어느 줄을 그리나. 해외는 시장값 한 줄이다 - 달러로 파는 것이 아니라 살때·팔때가 없다. */
    series() {
      return this.isDomestic ? LINES : [OVERSEAS_LINE]
    },

    /** 겹쳐 보기면 한 칸, 나눠 보기면 줄마다 한 칸. */
    panels() {
      if (!this.isDomestic) return [{ key: 'all', name: '' }]
      return this.mode === 'overlay' ? [{ key: 'all', name: '' }] : LINES
    },

    /** 요약 줄에서 값을 꺼낼 열쇠. 해외는 rawPrice 다. */
    valueKey() {
      return this.isDomestic ? 'base' : 'rawPrice'
    },

    latest() {
      return this.shown.length ? this.shown[this.shown.length - 1] : null
    },

    /** 기간 안의 최고·최저. 국내는 기본가격, 해외는 시장값 기준이다. */
    span() {
      if (!this.shown.length) return null
      const key = this.valueKey
      let min = Infinity
      let max = -Infinity
      let minAt = ''
      let maxAt = ''
      for (const p of this.shown) {
        const v = Number(p[key])
        if (!Number.isFinite(v)) continue
        if (v < min) { min = v; minAt = p.at }
        if (v > max) { max = v; maxAt = p.at }
      }
      if (!Number.isFinite(min)) return null
      return { min, max, minAt, maxAt }
    },

    /** 기간 시작 대비 지금. 올랐는지 내렸는지만 본다. */
    change() {
      if (this.shown.length < 2) return null
      const key = this.valueKey
      const first = Number(this.shown[0][key])
      const last = Number(this.shown[this.shown.length - 1][key])
      if (!Number.isFinite(first) || !first || !Number.isFinite(last)) return null
      return { diff: last - first, percent: ((last - first) / first) * 100 }
    },

    /** 등급별 시세표를 금속별로 묶는다. 표에 소제목 줄을 넣기 위해서다. */
    boardGroups() {
      const order = []
      const bucket = {}
      for (const r of this.boardRows) {
        if (!bucket[r.metal]) { bucket[r.metal] = []; order.push(r.metal) }
        bucket[r.metal].push(r)
      }
      return order.map((m) => ({
        metal: m,
        label: (METALS.find((x) => x.key === m) || {}).label || m,
        basis: bucket[m][0]?.basis,
        rows: bucket[m],
      }))
    },
  },

  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.load()
    this.loadBoard()
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    this.disposeAll()
  },

  methods: {
    // ---------------------------------------------------------------- 조작

    onTenant() {
      this.load()
      this.loadBoard()
    },

    onMetal(key) {
      this.metal = key
      // 보는 방식(국내/해외)은 그대로 둔다. 금속마다 둘 다 있다.
      this.load()
    },

    onView() {
      // 줄 수가 바뀌므로(국내 셋 / 해외 하나) 인스턴스를 버리고 다시 만든다.
      this.disposeAll()
      this.load()
    },

    onMode() {
      // 칸 수가 바뀌므로 인스턴스를 버리고 다시 만든다.
      this.disposeAll()
      this.$nextTick(this.render)
    },

    // ---------------------------------------------------------------- 읽기

    async load() {
      this.loading = true
      this.errorMessage = ''
      try {
        const chosen = RANGES.find((r) => r.key === this.range) || RANGES[1]
        const to = new Date()
        const from = chosen.days
          ? new Date(to.getTime() - chosen.days * 86400000)
          // '전체' 는 수집을 시작하기 한참 전으로 잡는다. 서버가 있는 만큼만 준다.
          : new Date('2015-01-01T00:00:00Z')

        const res = await getPriceHistory({
          tenant: this.tenant,
          metal: this.metal,
          // 해외는 언제나 해외 현물(SPOT)에서 온다. 국내는 금만 KRX 이고
          // 은·백금은 KRX 에 시장이 없어 역시 SPOT 을 환산해 쓴다.
          basis: this.isDomestic ? this.metalInfo.domesticBasis : 'SPOT',
          from: from.toISOString(),
          to: to.toISOString(),
          interval: this.interval,
        })
        if (res.data?.code !== OK) {
          throw new Error(res.data?.message || '시세 이력을 불러오지 못했습니다.')
        }
        const data = res.data.data || {}
        this.points = data.points || []
        this.priced = this.points.every((p) => p.priced !== false)
        this.$nextTick(this.render)
      } catch (e) {
        this.points = []
        this.disposeAll()
        this.errorMessage = e?.response?.data?.message || e.message || '시세 이력을 불러오지 못했습니다.'
      } finally {
        this.loading = false
      }
    },

    async loadBoard() {
      this.boardLoading = true
      try {
        const res = await getPriceBoard(this.tenant, 3.75)
        if (res.data?.code !== OK) {
          throw new Error(res.data?.message || '시세표를 불러오지 못했습니다.')
        }
        const data = res.data.data || {}
        this.boardRows = data.rows || []
        this.boardAt = data.servedAt || ''
      } catch (e) {
        this.boardRows = []
        // 표가 비는 것만으로 충분하다. 차트가 떠 있는데 빨간 띠를 또 띄울 일은 아니다.
        console.warn('시세표를 불러오지 못했습니다.', e)
      } finally {
        this.boardLoading = false
      }
    },

    // ---------------------------------------------------------------- 그리기

    /**
     * 칸마다 인스턴스를 붙인다.
     *
     * DOM 에서 떨어졌다 돌아온 인스턴스는 크기를 0 으로 잡으므로 버리고 새로 만든다
     * (기존 시세 화면에서 겪은 것과 같은 자리다).
     */
    render() {
      if (!this.shown.length) {
        this.disposeAll()
        return
      }
      const panels = this.panels
      const next = []
      for (let i = 0; i < panels.length; i++) {
        const dom = this.$refs[`panel-${panels[i].key}`]?.[0] || this.$refs[`panel-${panels[i].key}`]
        if (!dom) continue
        let inst = this.charts[i]
        if (inst && (inst.isDisposed() || !inst.getDom()?.isConnected)) {
          inst.dispose()
          inst = null
        }
        if (!inst || inst.getDom() !== dom) {
          if (inst) inst.dispose()
          inst = echarts.init(dom, null, { renderer: 'canvas' })
        }
        // 해외는 줄이 하나라 나눌 것이 없다. 국내일 때만 보기 모드를 본다.
        const series = !this.isDomestic || this.mode === 'overlay' ? this.series : [panels[i]]
        inst.setOption(this.buildOption(series), true)
        next.push(inst)
      }
      // 칸이 줄었으면 남는 인스턴스를 버린다.
      for (let i = next.length; i < this.charts.length; i++) {
        this.charts[i]?.dispose()
      }
      this.charts = next
    },

    /**
     * @param {Array<{key:string,name:string,color:string}>} lines 이 칸에 그릴 줄
     */
    buildOption(lines) {
      const labels = this.shown.map((p) => this.axisLabel(p.at))
      const unitText = this.viewText.unit
      // 달러는 원보다 자릿수가 훨씬 작다. 소수 둘째 자리까지 보여야 움직임이 읽힌다.
      const fmt = (v) => Number(v).toLocaleString('ko-KR',
        this.isDomestic ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 })

      return {
        backgroundColor: 'transparent',
        grid: { top: 28, right: 24, bottom: 56, left: 20, containLabel: true },
        legend: lines.length > 1
          // 범례 막대도 선만큼 굵게. 여기가 색을 가장 먼저 읽는 자리다.
          ? { top: 0, textStyle: { color: '#dbe5f2', fontSize: 13 }, itemWidth: 26, itemHeight: 12 }
          : { show: false },

        xAxis: {
          type: 'category',
          data: labels,
          boundaryGap: false,
          axisLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.45)' } },
          axisTick: { show: false },
          axisLabel: {
            color: '#a9b8cf',
            fontSize: 11,
            rotate: 30,
            margin: 10,
            interval: Math.max(0, Math.floor(labels.length / 9) - 1),
          },
          splitLine: { show: false },
        },

        yAxis: {
          type: 'value',
          name: unitText,
          // 0 부터 그리면 변동폭이 바닥에 붙어 평평해 보인다. scale 이 실제 범위에
          // 맞춰 주고 boundaryGap 이 위아래 여유를 둔다.
          //
          // 직접 min/max 를 계산해 넣지 않는다. 그러면 그 값이 그대로 맨 위 눈금이
          // 되어 "204,439" 같은 숫자가 축에 뜬다 - ECharts 가 고르면 둥근 수가 된다.
          scale: true,
          boundaryGap: ['8%', '8%'],
          nameTextStyle: { color: '#9fb0c9', fontSize: 11, align: 'right' },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#a9b8cf',
            fontSize: 10,
            formatter: fmt,
          },
          splitLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.24)', type: 'dashed' } },
        },

        tooltip: {
          trigger: 'axis',
          axisPointer: { lineStyle: { color: 'rgba(203, 213, 225, 0.85)', type: 'dashed' } },
          backgroundColor: 'rgba(15, 23, 42, 0.96)',
          borderColor: 'rgba(148, 163, 184, 0.5)',
          borderWidth: 1,
          padding: [10, 14],
          textStyle: { color: '#e5ecf8', fontSize: 12 },
          formatter: (params) => {
            if (!params?.length) return ''
            const head = `<div style="font-weight:600;color:#f8fafc;margin-bottom:4px;">${params[0].axisValue}</div>`
            const body = params.map((p) => {
              const v = p.value == null ? '-' : `${fmt(p.value)} ${unitText}`
              return `<div style="display:flex;align-items:center;gap:6px;">
                        <span style="width:8px;height:8px;border-radius:50%;background:${p.color};display:inline-block;"></span>
                        <span style="color:#b8c7dc;">${p.seriesName}</span>
                        <span style="margin-left:auto;padding-left:20px;font-weight:600;color:#f8fafc;">${v}</span>
                      </div>`
            }).join('')
            return head + body
          },
        },

        series: lines.map((l) => ({
          name: l.name,
          type: 'line',
          smooth: true,
          data: this.shown.map((p) => (p[l.key] == null ? null : Number(p[l.key]))),
          lineStyle: { color: l.color, width: LINE_WIDTH },
          itemStyle: { color: l.color },
          symbol: 'circle',
          symbolSize: 7,
          // 점이 적으면 찍어 준다. 하나뿐이면 선이 안 그려지므로 점이라도 보여야 한다.
          showSymbol: this.shown.length <= 40,
          // 겹쳐 그릴 때는 면을 칠하지 않는다. 세 줄이 가까워 서로를 가린다.
          areaStyle: lines.length > 1 ? undefined : {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: this.fade(l.color, 0.35) },
              { offset: 1, color: this.fade(l.color, 0.02) },
            ]),
          },
        })),
      }
    },

    /** #rrggbb 를 rgba 로. 면 그라데이션에만 쓴다. */
    fade(hex, alpha) {
      const n = parseInt(hex.replace('#', ''), 16)
      return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
    },

    handleResize() {
      for (const c of this.charts) {
        if (c && !c.isDisposed()) c.resize()
      }
    },

    disposeAll() {
      for (const c of this.charts) {
        if (c && !c.isDisposed()) c.dispose()
      }
      this.charts = []
    },

    // ---------------------------------------------------------------- 표시

    /** 일 단위면 MM-DD, 시간 단위면 MM-DD HH시. */
    axisLabel(iso) {
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return String(iso || '').slice(0, 10)
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      if (this.interval === 'hour') {
        return `${mm}-${dd} ${String(d.getHours()).padStart(2, '0')}시`
      }
      return `${mm}-${dd}`
    },

    money(v) {
      if (v == null || v === '') return '-'
      return Number(v).toLocaleString('ko-KR')
    },

    /**
     * 지금 보는 통화에 맞춰 찍는다.
     *
     * 달러는 원보다 자릿수가 훨씬 작다 — 은이 57.60 USD/ozt 인데 정수로 깎으면
     * 58 이 되어 하루 움직임이 통째로 사라진다. 소수 둘째 자리까지 쓴다.
     */
    val(v) {
      if (v == null || v === '') return '-'
      if (this.isDomestic) return Number(v).toLocaleString('ko-KR')
      return Number(v).toLocaleString('ko-KR',
        { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    /** val() 에 부호를 단 것. 기간 변동이 쓴다. */
    signedVal(v) {
      if (v == null) return '-'
      return `${Number(v) > 0 ? '+' : ''}${this.val(v)}`
    },

    signed(v) {
      if (v == null) return '-'
      const n = Number(v)
      return `${n > 0 ? '+' : ''}${n.toLocaleString('ko-KR', { maximumFractionDigits: 0 })}`
    },

    /** +8.21% — 소수 둘째 자리까지. signed() 로 찍으면 0 자리로 깎여 +8% 가 된다. */
    percent(v) {
      if (v == null) return '-'
      const n = Number(v)
      return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`
    },

    dayText(iso) {
      if (!iso) return '-'
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10)
      return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
    },

    timeText(iso) {
      if (!iso) return '-'
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return String(iso)
      return `${this.dayText(iso)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    },

    changeClass(n) {
      if (n == null) return ''
      return n > 0 ? 'mc-up' : n < 0 ? 'mc-down' : ''
    },
  },
}
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center mb-5">
      <div>
        <div class="text-h5 font-weight-bold">시세 차트</div>
        <div class="text-body-2 text-grey mt-1">
          국내는 기본가격 · 살 때 · 팔 때(원/g), 해외는 바깥 시장 값(USD/트로이온스)
        </div>
      </div>
      <v-spacer />
      <v-btn
        variant="tonal"
        color="amber-darken-2"
        prepend-icon="mdi-cog-outline"
        to="/market/fee-policies"
      >수수료 설정</v-btn>
    </div>

    <v-alert type="info" variant="tonal" density="comfortable" class="mb-5" border="start">
      <div class="text-body-2">
        <strong>국내</strong>는 우리가 파는 값입니다 — <strong>원/g</strong> 에 수수료가
        붙어 있고 기본가격 · 살 때 · 팔 때 세 줄이 나옵니다.
        <strong>해외</strong>는 바깥 시장이 매긴 값입니다 — <strong>USD/트로이온스</strong>
        한 줄이고 환율도 수수료도 붙지 않습니다.
      </div>
      <div class="text-body-2 mt-1">
        국내 시세를 어디서 가져오는지는 금속마다 다릅니다. <strong>금</strong>은
        KRX 금현물에서 바로 오고, <strong>은·백금</strong>은 KRX 에 시장이 없어
        해외 현물을 원화로 환산한 것이 국내 시세입니다.
      </div>
      <div class="text-body-2 mt-1">
        아래 등급별 시세표만 <strong>원/돈(3.75g)</strong> 입니다. 돈 값은 환산율과 무게를
        곱한 뒤 서버가 한 번만 깎은 것이라, 차트 값에 3.75 를 곱한 것과 끝자리가 다를 수 있습니다.
      </div>
      <div class="text-body-2 mt-1">
        모든 금액에 <strong>부가세는 포함되어 있지 않습니다.</strong>
      </div>
    </v-alert>

    <v-alert
      v-if="errorMessage"
      type="error"
      variant="tonal"
      density="comfortable"
      class="mb-4"
      closable
      @click:close="errorMessage = ''"
    >{{ errorMessage }}</v-alert>

    <v-alert
      v-if="isDomestic && !priced && points.length"
      type="warning"
      variant="tonal"
      density="comfortable"
      class="mb-4"
      border="start"
    >
      수수료 정책이 걸려 있지 않은 구간이 있습니다. 그 구간은 세 줄이 기준 시세와 같습니다.
    </v-alert>

    <!-- 법인 -->
    <v-tabs v-model="tenant" color="amber-darken-2" class="mb-4" @update:model-value="onTenant">
      <v-tab v-for="t in TENANTS" :key="t.key" :value="t.key">{{ t.label }}</v-tab>
    </v-tabs>

    <!-- 고르는 줄 -->
    <v-card rounded="lg" elevation="0" border class="mb-4">
      <v-card-text class="d-flex flex-wrap align-center" style="gap: 20px 28px">
        <div class="d-flex align-center" style="gap: 10px">
          <span class="text-caption text-grey">금속</span>
          <v-btn-toggle :model-value="metal" density="comfortable" variant="outlined" divided
                        color="amber-darken-2" mandatory>
            <v-btn v-for="m in METALS" :key="m.key" :value="m.key" size="small"
                   @click="onMetal(m.key)">{{ m.label }}</v-btn>
          </v-btn-toggle>
        </div>

        <!-- 어느 금속이든 둘 다 있다 — 국내는 우리가 파는 원화 값,
             해외는 바깥 시장의 USD 값. -->
        <div class="d-flex align-center" style="gap: 10px">
          <span class="text-caption text-grey">보는 값</span>
          <v-btn-toggle v-model="view" density="comfortable" variant="outlined" divided
                        color="amber-darken-2" mandatory @update:model-value="onView">
            <v-btn v-for="v in VIEWS" :key="v.key" :value="v.key" size="small">{{ v.label }}</v-btn>
          </v-btn-toggle>
        </div>

        <div class="d-flex align-center" style="gap: 10px">
          <span class="text-caption text-grey">기간</span>
          <v-btn-toggle v-model="range" density="comfortable" variant="outlined" divided
                        color="amber-darken-2" mandatory @update:model-value="load">
            <v-btn v-for="r in RANGES" :key="r.key" :value="r.key" size="small">{{ r.label }}</v-btn>
          </v-btn-toggle>
        </div>

        <div class="d-flex align-center" style="gap: 10px">
          <span class="text-caption text-grey">단위</span>
          <v-btn-toggle v-model="interval" density="comfortable" variant="outlined" divided
                        color="amber-darken-2" mandatory @update:model-value="load">
            <v-btn value="day" size="small">일</v-btn>
            <v-btn value="hour" size="small">시간</v-btn>
          </v-btn-toggle>
        </div>

        <!-- 해외는 줄이 하나라 나눌 것이 없다. 국내일 때만 보인다. -->
        <div v-if="isDomestic" class="d-flex align-center" style="gap: 10px">
          <span class="text-caption text-grey">보기</span>
          <v-btn-toggle v-model="mode" density="comfortable" variant="outlined" divided
                        color="amber-darken-2" mandatory @update:model-value="onMode">
            <v-btn value="overlay" size="small">한 장에 겹쳐</v-btn>
            <v-btn value="split" size="small">세 장으로</v-btn>
          </v-btn-toggle>
        </div>
      </v-card-text>
    </v-card>

    <!-- 지금 값 -->
    <v-card v-if="latest" rounded="lg" elevation="0" border class="mb-4">
      <v-card-text class="d-flex flex-wrap" style="gap: 8px 44px">
        <!-- 국내는 세 줄, 해외는 시장값 한 줄. -->
        <div v-for="l in series" :key="l.key">
          <div class="text-caption text-grey">{{ l.name }}</div>
          <div class="text-h6 font-weight-bold" :style="{ color: l.color }">
            {{ val(latest[l.key]) }}
            <span class="text-caption font-weight-regular">{{ viewText.unit }}</span>
          </div>
        </div>
        <div v-if="change">
          <div class="text-caption text-grey">기간 변동</div>
          <div class="text-h6 font-weight-bold" :class="changeClass(change.diff)">
            {{ signedVal(change.diff) }}
            <span class="text-caption font-weight-regular">
              ({{ percent(change.percent) }})
            </span>
          </div>
        </div>
        <div v-if="span">
          <div class="text-caption text-grey">기간 최고 · 최저</div>
          <div class="text-body-1 font-weight-medium mt-1">
            {{ val(span.max) }} <span class="text-caption text-grey">{{ dayText(span.maxAt) }}</span>
            &nbsp;·&nbsp;
            {{ val(span.min) }} <span class="text-caption text-grey">{{ dayText(span.minAt) }}</span>
          </div>
        </div>
        <v-spacer />
        <div class="text-right">
          <div class="text-caption text-grey">마지막 점</div>
          <div class="text-body-2 mt-1">{{ timeText(latest.at) }}</div>
          <div class="text-caption text-grey">{{ latest.source }}</div>
        </div>
      </v-card-text>
    </v-card>

    <!-- 차트 -->
    <v-card rounded="lg" elevation="0" border class="mb-6">
      <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
        {{ metalInfo.label }} · {{ viewText.label }}
        <span class="text-caption text-grey ml-2">{{ viewText.hint }}</span>
        <v-spacer />
        <span v-if="shown.length" class="text-caption text-grey">{{ shown.length }}점</span>
      </v-card-title>
      <v-card-text>
        <div v-if="loading" class="mc-empty">
          <v-progress-circular indeterminate color="amber-darken-2" size="28" />
        </div>
        <div v-else-if="!shown.length" class="mc-empty text-body-2 text-grey text-center">
          <div>
            <div>이 기간에 그릴 시세가 없습니다.</div>
            <div v-if="droppedForUsd" class="text-caption mt-2">
              원화 값은 {{ droppedForUsd }}점 있지만 USD 원본이 없습니다 —
              국내로 보면 나옵니다.
            </div>
          </div>
        </div>
        <template v-else>
          <!-- 해외는 USD 원본이 있는 점만 그린다. 7개월 구간은 metals.dev 로 이미
               원화로 환산해 받아 둬서 USD 를 복원할 수 없다. 조용히 빼지 않고 말해 준다. -->
          <v-alert
            v-if="droppedForUsd"
            type="info"
            variant="tonal"
            density="compact"
            border="start"
            class="mb-3"
          >
            <div class="text-body-2">
              이 기간의 <strong>{{ droppedForUsd }}점</strong>은 USD 원본이 없어 빠졌습니다.
              2026-10-18 이전은 해외 시세를 <strong>이미 원화로 환산해</strong> 받아 둬서
              달러 값을 되살릴 수 없습니다. <strong>국내</strong>로 보면 그 구간도 다 나옵니다.
            </div>
          </v-alert>

          <!-- 점이 한둘이면 선이 안 그려진다. 빈 화면처럼 보이니 이유를 적어 준다
               (2026-10-19: 국내 금이 1점이라 "국내시세가 없다" 는 제보가 왔다). -->
          <v-alert
            v-if="shown.length <= 2"
            type="info"
            variant="tonal"
            density="compact"
            border="start"
            class="mb-3"
          >
            <div class="text-body-2">
              이 기간에 쌓인 점이 <strong>{{ shown.length }}개</strong>뿐이라 선이 그려지지
              않습니다. 점으로만 보입니다.
            </div>
            <div v-if="isDomestic && metalInfo.domesticBasis === 'KRX'" class="text-body-2 mt-1">
              KRX 금현물은 <strong>장이 열려 있는 동안에만</strong> 값이 바뀝니다 —
              휴장일과 장 시작 전에는 직전 거래일 값에 멈춰 있습니다.
            </div>
            <div v-else class="text-body-2 mt-1">
              시세를 모으기 시작한 지 얼마 되지 않았습니다. 날마다 쌓입니다.
            </div>
          </v-alert>

          <div v-for="p in panels" :key="p.key" class="mb-2">
            <div v-if="isDomestic && mode === 'split'" class="text-body-2 font-weight-medium mb-1"
                 :style="{ color: p.color }">{{ p.name }}</div>
            <div :ref="`panel-${p.key}`" class="mc-chart"
                 :class="{ 'mc-chart-small': isDomestic && mode === 'split' }" />
          </div>
        </template>
      </v-card-text>
    </v-card>

    <!-- 등급별 시세표. 밖으로 나가는 값을 그대로 보여 준다. -->
    <v-card rounded="lg" elevation="0" border>
      <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
        등급별 시세표
        <span class="text-caption text-grey ml-2">
          원/돈(3.75g) — 홈페이지 헤더의 "오늘의 거래 기준가" 와 같은 값입니다
        </span>
        <v-spacer />
        <span v-if="boardAt" class="text-caption text-grey">{{ timeText(boardAt) }}</span>
      </v-card-title>
      <v-card-text>
        <div v-if="boardLoading" class="text-body-2 text-grey">불러오는 중…</div>
        <div v-else-if="!boardRows.length" class="text-body-2 text-grey">
          시세표를 불러오지 못했습니다.
        </div>
        <v-table v-else density="comfortable" class="mc-table">
          <thead>
            <tr>
              <th style="width: 180px">등급</th>
              <th class="text-right" style="width: 110px">환산율</th>
              <th class="text-right">기본가격</th>
              <th class="text-right">살 때</th>
              <th class="text-right">팔 때</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="g in boardGroups" :key="g.metal">
              <tr class="mc-group">
                <td colspan="5" class="text-caption text-grey">
                  {{ g.label }} · {{ g.basis === 'KRX' ? '국내 KRX' : '해외 현물' }}
                </td>
              </tr>
              <tr v-for="r in g.rows" :key="g.metal + r.grade">
                <!-- 은·백금은 이름과 등급이 같다(순은/순은). 같으면 한 번만 쓴다. -->
                <td>
                  {{ r.label }}
                  <span v-if="r.grade !== r.label" class="text-caption text-grey">{{ r.grade }}</span>
                </td>
                <td class="text-right">{{ Number(r.rate) }}</td>
                <td class="text-right">{{ money(r.base) }}</td>
                <td class="text-right mc-buy">{{ money(r.buy) }}</td>
                <td class="text-right mc-sell">{{ money(r.sell) }}</td>
              </tr>
            </template>
          </tbody>
        </v-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<style scoped>
.mc-chart {
  width: 100%;
  height: 420px;
}

/* 세 장으로 나눌 때. 셋이 한 화면에 들어와야 비교가 된다. */
.mc-chart-small {
  height: 220px;
}

.mc-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 240px;
}

/* 수수료 화면과 같은 색을 쓴다 — 두 화면에서 살 때가 다른 색이면 안 된다. */
/* 차트 선과 같은 색을 쓴다. 한 화면 안에서 살 때가 두 색이면 안 된다. */
.mc-buy {
  color: #ff6b5b;
}

.mc-sell {
  color: #4fa8ff;
}

.mc-up {
  color: #ff8a80;
}

.mc-down {
  color: #9ecbff;
}

.mc-table :deep(td),
.mc-table :deep(th) {
  white-space: nowrap;
}

.mc-group td {
  padding-top: 14px !important;
}
</style>
